"use strict";
Object.defineProperty(exports, "__esModule", { value: true });
exports.findRuleForTargets = findRuleForTargets;
exports.ruleRateLabel = ruleRateLabel;
exports.previewLineEarnings = previewLineEarnings;
exports.previewPlanEarnings = previewPlanEarnings;
exports.planTargetsPackagesSpecifically = planTargetsPackagesSpecifically;
const commissionPlan_1 = require("./commissionPlan");
const CENTS = (c) => `$${(c / 100).toFixed(2)}`;
/**
 * The rule for a line, trying every id most-specific-first before falling back
 * to the scope's wildcard.
 *
 * EXTRACTED FROM `commissionEmit.ts`, which had this exact loop inline. Rule
 * resolution now exists once: a preview that resolved differently from the pay
 * path would show an operator one rate and pay another, which is the whole
 * failure this file is meant to prevent.
 */
function findRuleForTargets(plan, scope, targetIds) {
    for (const id of targetIds || []) {
        if (!id)
            continue;
        const hit = (0, commissionPlan_1.findRule)(plan, scope, id);
        if (hit && hit.target !== commissionPlan_1.ANY_TARGET)
            return hit;
    }
    return (0, commissionPlan_1.findRule)(plan, scope, undefined);
}
/** How the rate reads to a human. Presentation only — never parsed back. */
function ruleRateLabel(rule) {
    if (!rule)
        return '—';
    if (rule.basis === 'flat_per_unit')
        return `${CENTS(rule.flatCents || 0)} per unit`;
    return `${rule.rate || 0}% of ${rule.basis === 'percent_of_margin' ? 'margin' : 'price'}`;
}
/**
 * WHICH MONEY A RULE IS APPLIED TO.
 *
 * A `once` rule pays on what changes hands ONCE — a device's up-front amount.
 * When a line has no up-front amount (an installment or rental device, a
 * package), the once-rule is applied to a single cycle of the monthly amount,
 * which is what `emitCommissionsForSale` does today: it hands the calculator
 * one `grossCents` per event, and for a monthly-billed thing that gross IS one
 * cycle.
 *
 * A `monthly` rule pays on ONE CYCLE, every cycle, so its base is the monthly
 * amount and never the up-front one — a deposit is not earned again each month.
 */
function baseFor(rule, line) {
    const monthly = Math.max(0, Math.trunc(line.monthlyCents || 0));
    const oneTime = Math.max(0, Math.trunc(line.oneTimeCents || 0));
    if (rule.recurrence === 'monthly')
        return { gross: monthly, recurring: true };
    return { gross: oneTime > 0 ? oneTime : monthly, recurring: false };
}
/**
 * `costCents` is stated for the WHOLE line, so a margin rule on a multi-unit
 * line compares like with like. Scaled with quantity alongside the price.
 */
function scaled(v, quantity) {
    return Math.trunc(v * quantity);
}
/** What one line earns. Every number comes from `computeRuleCommission`. */
function previewLineEarnings(plan, line) {
    const quantity = Math.max(1, Math.trunc(line.quantity ?? 1));
    const rule = findRuleForTargets(plan, line.scope, line.targetIds);
    const base = {
        key: line.key,
        label: line.label,
        scope: line.scope,
        ruleId: rule?.ruleId ?? null,
        specific: Boolean(rule && rule.target !== commissionPlan_1.ANY_TARGET),
        basis: rule?.basis,
        rateApplied: rule ? (rule.basis === 'flat_per_unit' ? rule.flatCents : rule.rate) : undefined,
        recurrence: rule?.recurrence ?? null,
        months: rule?.months ?? null,
        rateLabel: ruleRateLabel(rule),
        upfrontCents: null,
        perMonthCents: null,
        firstYearCents: null,
        reason: 'no_rule',
    };
    if (!rule) {
        return { ...base, problem: 'No rule in this plan covers it — it pays nothing.' };
    }
    const { gross, recurring } = baseFor(rule, line);
    const outcome = (0, commissionPlan_1.computeRuleCommission)(rule, {
        grossCents: scaled(gross, quantity),
        ...(typeof line.costCents === 'number' ? { costCents: scaled(line.costCents, quantity) } : {}),
    });
    if (outcome.commissionCents === null) {
        return {
            ...base,
            reason: outcome.reason,
            problem: outcome.reason === 'missing_cost'
                // Not zero, and said so: a margin rule with no landed cost is an
                // unanswerable question, not an answer of nothing.
                ? 'This rule pays on margin, but no landed cost is known for it — nothing can be paid until one is set.'
                : 'This line cannot be priced under the matched rule.',
        };
    }
    const amount = outcome.commissionCents;
    const upfront = recurring ? 0 : amount;
    const perMonth = recurring ? amount : 0;
    // Twelve is the horizon, not the term: a rule capped at 6 cycles contributes
    // 6, and an uncapped one contributes 12 rather than an invented "forever".
    const cycles = recurring ? Math.min(12, rule.months ?? 12) : 0;
    return {
        ...base,
        reason: outcome.reason,
        basis: outcome.basisApplied ?? rule.basis,
        rateApplied: outcome.rateApplied ?? base.rateApplied,
        upfrontCents: upfront,
        perMonthCents: perMonth,
        firstYearCents: upfront + perMonth * cycles,
        ...(amount === 0
            ? {
                problem: outcome.reason === 'non_positive_base'
                    ? 'Sold at or below cost, so there is no margin to share.'
                    : 'This rule computes to nothing on this line.',
            }
            : {}),
    };
}
/** Every line, plus the totals a partner actually asks about. */
function previewPlanEarnings(plan, lines) {
    const rows = (lines || []).map((l) => previewLineEarnings(plan, l));
    const totals = {
        upfrontCents: 0, perMonthCents: 0, firstYearCents: 0, unmatched: 0, unpriceable: 0,
    };
    for (const r of rows) {
        if (r.ruleId === null) {
            totals.unmatched += 1;
            continue;
        }
        if (r.upfrontCents === null || r.perMonthCents === null) {
            totals.unpriceable += 1;
            continue;
        }
        totals.upfrontCents += r.upfrontCents;
        totals.perMonthCents += r.perMonthCents;
        totals.firstYearCents += r.firstYearCents ?? 0;
    }
    return { rows, totals };
}
/**
 * Does this plan name individual packages?
 *
 * THE HONEST-CALCULATOR SWITCH. `/onboarding/complete` emits ONE
 * `software_package` line for the whole monthly commitment with no target, so
 * a rule aimed at a specific packageId cannot match it today — only the
 * scope's wildcard can. A calculator that showed "18% on Compliance, 10% on
 * everything else" would therefore be describing money that does not currently
 * move that way.
 *
 * Callers use this to say so plainly next to the numbers, rather than either
 * hiding the per-package view or presenting it as settled fact.
 */
function planTargetsPackagesSpecifically(plan) {
    return (plan?.rules ?? []).some((r) => r.scope === 'software_package' && r.target !== commissionPlan_1.ANY_TARGET);
}
//# sourceMappingURL=commissionPreview.js.map